'use client';

import { Carousel } from '@/decodo/components/atoms/Carousel';
import { useRef, useState } from 'react';
import { Autoplay } from 'swiper/modules';
import { SwiperRef } from 'swiper/react';
import { MenuCarouselPagination } from './MenuCarouselPagination';
import { MenuCarouselItem, MenuCarouselItemProps } from './MenuCarouseltem';

export type MenuCarouselProps = {
  items: Pick<MenuCarouselItemProps, 'image' | 'link'>[];
};

export function MenuCarousel({ items }: MenuCarouselProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const swiperRef = useRef<SwiperRef>(null);

  const slidesRefMap = useRef(new Map<number, HTMLAnchorElement | null>());

  const updateSlide = (index: number) => {
    if (!swiperRef.current) return;
    swiperRef.current.swiper.slideTo(index);
    setCurrentSlideIndex(index);
    slidesRefMap.current.get(index)?.focus();
  };

  return (
    <section className='flex flex-col items-center gap-y-3'>
      <Carousel
        ref={swiperRef}
        sideGradient={false}
        classNames={{
          root: 'w-full aspect-[300/158]',
          swiper: 'w-full',
          swiperSlide: 'w-full',
        }}
        config={{
          'aria-label': 'Menu Carousel',
          role: 'region',
          slidesPerView: 1,
          modules: [Autoplay],
          autoplay: {
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          },
          loop: true,
          onRealIndexChange: ({ realIndex }) => setCurrentSlideIndex(realIndex),
        }}
        items={items}
        renderItem={({ item, index: i }) => (
          <MenuCarouselItem
            key={i}
            image={item.image}
            link={item.link}
            isActive={currentSlideIndex === i}
            ref={(node) => {
              slidesRefMap.current.set(i, node);
              return () => {
                slidesRefMap.current.delete(i);
              };
            }}
          />
        )}
      />
      {/* TODO: separately fix the weird behaviour when clicking 1 -> 3 */}
      <MenuCarouselPagination
        activeIndex={currentSlideIndex}
        itemCount={items.length}
        handlePageChange={updateSlide}
      />
    </section>
  );
}
